import React, { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Tile, Button, Tag, DataTable, Table, TableHead, TableRow, TableHeader, TableBody, TableCell, Loading, Toggle, Pagination, Modal } from '@carbon/react';
import { ArrowLeft, Wifi, Location, Time, CheckmarkFilled, CloudOffline, Temperature, Humidity, Restart, TrashCan, Devices, Add, Settings } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useSubscription } from '../hooks/useSubscription';
import SubscriptionGate from '../components/SubscriptionGate';
import { useRedisGatewayDetail, DATA_WINDOW_MINUTES } from '../hooks/useRedisGatewayDetail';
import { useSensorsOptimized } from '../hooks/useSensorsOptimized';
import { useRedisReadings } from '../hooks/useRedisReadings';
import { formatRelativeTime } from '../lib/timeUtils';
import { getBatteryPercentage } from '../lib/batteryUtils';
import { convertGatewayTemperature, convertGatewayHumidity } from '../lib/sensorReadingUtils';
import { getSigmawitT201Readings, parseSigmawitT201Advertisement } from '../lib/sigmawitParser';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabaseClient';
import GatewayTypeTags from '../components/GatewayTypeTags';
import { getGatewayProductFamily } from '../utils/gatewayProductLabel';
import './GatewayDetailPage.scss';

const GatewayDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { user } = useAuth();
  const { hasActiveSubscription, loading: subLoading } = useSubscription();

  // Pagination state for sensor assignment
  const [sensorPage, setSensorPage] = useState(1);
  const [sensorPageSize, setSensorPageSize] = useState(10);
  
  // Pagination state for connected devices table
  const [devicesPage, setDevicesPage] = useState(1);
  const [devicesPageSize, setDevicesPageSize] = useState(10);
  
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [activeShipmentBinding, setActiveShipmentBinding] = useState(null);
  
  // Format timestamp with both actual time and relative time - converts to local time
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return 'Never';
    
    // Format actual time in LOCAL timezone: "Jan 18, 2:30 PM"
    const actualTime = date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    
    // Format relative time: "2 mins ago"
    const relativeTime = formatRelativeTime(date);
    
    return `${actualTime} (${relativeTime})`;
  };
  
  // Simplified approach: Use gateway_sensor_readings for accurate battery, source, and registration
  // Get gateway info and connected sensors with their readings from gateway_sensor_readings table
  const {
    gateway,
    connectedSensors,
    gatewaySensorReadings,
    gatewayHealth,
    gatewayDataStatus,
    gatewayConnectionSource,
    isLoading,
    isToggling,
    error,
    handleSensorToggle
  } = useRedisGatewayDetail(id);

  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;

    supabase
      .from('shipment_gateway_bindings')
      .select('*, shipments(id, epc_uri, status, metadata)')
      .eq('gateway_id', id)
      .is('effective_to', null)
      .order('effective_from', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setActiveShipmentBinding(data);
      })
      .catch(() => {
        if (!cancelled) setActiveShipmentBinding(null);
      });

    return () => { cancelled = true; };
  }, [id]);

  // Get all sensors to allow assignment - but memoize to prevent re-fetching
  const { sensors: allSensors, isLoading: sensorsLoading } = useSensorsOptimized();
  
  // Use the gateway sensor readings data from the hook
  // This provides accurate battery voltage, source, timestamp, and registration status
  const sensorsWithReadings = useMemo(() => {
    if (!connectedSensors || connectedSensors.length === 0) return [];
    
    return connectedSensors.map(sensor => {
      // Get latest reading from gateway_sensor_readings (from the hook)
      const gatewayReading = gatewaySensorReadings[sensor.id];
      
      // Fallback to optimized sensors data if no gateway reading
      const optimizedSensor = allSensors?.find(s => s.id === sensor.id);
      const latestReading = optimizedSensor?.latest_reading || null;
      
      // Use recorded_at from latest_reading (consistent with React Native and DeviceListPage)
      // Supabase timestamps are in UTC, new Date() automatically converts to local browser time
      const readingTime = latestReading?.recorded_at 
        ? new Date(latestReading.recorded_at)
        : null;

      // Use React Native SensorCard consistent logic:
      // - Online (green): data within 15 minutes
      // - Warning/Stale (yellow): data between 15-60 minutes
      // - Offline (red): data older than 60 minutes
      const getDeviceStatus = (time) => {
        if (!time) return 'offline';
        const now = Date.now();
        const readingMs = time.getTime();
        // Use Math.max to prevent negative values (handles future dates/timezone issues)
        const diffMs = Math.max(0, now - readingMs);
        const diffMins = Math.floor(diffMs / 60000);

        if (diffMins <= 15) return 'online';  // Within 15 min = online
        if (diffMins <= 60) return 'warning'; // 15-60 min = stale/warning
        return 'offline'; // > 60 min = offline
      };
      
      const deviceStatus = getDeviceStatus(readingTime);
      const isRecent = deviceStatus !== 'offline';
      
      // Get battery voltage from gateway_sensor_readings (this is the raw mV value)
      const batteryVoltage = gatewayReading?.battery_voltage ?? latestReading?.battery_level ?? null;
      
      // Get device type - prefer from gateway_sensor_readings, then sensor, then from sensor data
      let deviceType = gatewayReading?.device_type || sensor.device_type || optimizedSensor?.device_type;
      
      // Calculate battery percentage using the utility function
      const batteryPercentage = batteryVoltage ? getBatteryPercentage(batteryVoltage, deviceType) : null;
      
      // Get temperature and humidity from gateway reading or fallback
      const temperature = gatewayReading?.temperature ?? latestReading?.temperature ?? null;
      const humidity = gatewayReading?.humidity ?? latestReading?.humidity ?? null;
      
      // Get source from gateway_sensor_readings (mqtt, http, app)
      const source = gatewayReading?.source || latestReading?.source || null;
      
      // Determine registration status:
      // 1. If sensor exists in connectedSensors (from sensors table), it's REGISTERED
      // 2. Otherwise, check gateway_sensor_readings.registration_status
      // 3. If sensor has an ID in sensors table, it's registered regardless of online/offline status
      const registrationStatus = sensor.id ? 'registered' : (gatewayReading?.registration_status || null);
      
      // Format timestamp - use recorded_at from latest_reading (consistent with React Native)
      // Supabase timestamps are in UTC, new Date() will convert to local browser time automatically
      const timestampValue = latestReading?.recorded_at || latestReading?.created_at || null;
      const formattedProcessedAt = timestampValue ? formatTimestamp(timestampValue) : 'No data';
      
      return {
        ...sensor,
        latestReading,
        readingTime,
        deviceStatus,
        isRecent,
        batteryLevel: batteryVoltage, // Raw mV value
        batteryPercentage, // Calculated percentage
        deviceType,
        temperature,
        humidity,
        // Add properties needed for rendering
        temp: temperature, // For backward compatibility
        humidity: humidity, // For backward compatibility  
        battery: batteryPercentage, // Use calculated percentage
        source: source ? source.toUpperCase() : 'UNKNOWN',
        status: deviceStatus,
        registrationStatus,
        processedAt: formattedProcessedAt
      };
    });
    // Using .length to prevent re-computation on array reference changes
    // formatTimestamp is stable function, doesn't need to be in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectedSensors.length, gatewaySensorReadings, allSensors?.length]);

  // Format uptime
  const formatUptime = (seconds) => {
    if (!seconds) return '—';
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (d > 0) return `${d}d ${h}h ${m}m`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  // Format heartbeat time
  const formatHeartbeatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const d = new Date(timestamp);
    if (isNaN(d.getTime())) return 'N/A';
    const m = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
    if (m === 0) return 'Just now';
    if (m < 60) return `${m} min ago`;
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m ago`;
  };

  // Get display identifier for sensor (like React Native app)
  const getDisplayIdentifier = (sensor) => {
    if (sensor.device_type === 'kbeacon' && sensor.kbeacon_mac_key) {
      return sensor.kbeacon_mac_key;
    } else if (sensor.device_type === 'ela' && sensor.ela_name_key) {
      return sensor.ela_name_key;
    } else {
      return sensor.mac_address || sensor.device_id || '—';
    }
  };

  // Get sensor display name (clean up name)
  const getSensorDisplayName = (sensor) => {
    if (!sensor?.name) return sensor?.device_id || 'Unknown';
    const name = sensor.name;
    // Remove "sensorpush" prefix if present
    return name.replace(/\bsensorpush[\s_-]*/i, '').trim() || name;
  };

  // Paginated sensors for assignment list
  const paginatedSensors = useMemo(() => {
    if (!allSensors || allSensors.length === 0) return [];
    
    const startIndex = (sensorPage - 1) * sensorPageSize;
    const endIndex = startIndex + sensorPageSize;
    return allSensors.slice(startIndex, endIndex);
  }, [allSensors, sensorPage, sensorPageSize]);

  const totalSensorPages = Math.ceil((allSensors?.length || 0) / sensorPageSize);

  // Pagination calculations for connected devices table
  const totalDevices = sensorsWithReadings.length;
  const devicesStartIndex = (devicesPage - 1) * devicesPageSize;
  const devicesEndIndex = devicesStartIndex + devicesPageSize;
  const paginatedDevices = sensorsWithReadings.slice(devicesStartIndex, devicesEndIndex);

  // Status counts matching SensorCard logic: online (<15min), warning (15-60min), offline (>60min)
  const onlineCount = sensorsWithReadings.filter(d => d.status === 'online').length;
  const warningCount = sensorsWithReadings.filter(d => d.status === 'warning').length;
  const offlineCount = sensorsWithReadings.filter(d => d.status === 'offline').length;

  // Get status icon matching SensorCard 3-tier logic
  const getStatusIcon = (status) => {
    if (status === 'online') {
      return <CheckmarkFilled size={16} style={{ color: '#24a148' }} />; // Green - online
    } else if (status === 'warning') {
      return <CheckmarkFilled size={16} style={{ color: '#f1c21b' }} />; // Yellow - stale/warning
    }
    return <CloudOffline size={16} style={{ color: '#da1e28' }} />; // Red - offline
  };

  // Get status color for text/badges
  const getStatusColor = (status) => {
    if (status === 'online') return '#24a148'; // Green
    if (status === 'warning') return '#f1c21b'; // Yellow
    return '#da1e28'; // Red
  };

  // Get status tag type for Carbon Design
  const getStatusTagType = (status) => {
    if (status === 'online') return 'green';
    if (status === 'warning') return 'warm-gray'; // Yellow-ish
    return 'red';
  };

  const getBatteryColor = (percentage) => {
    if (percentage === null || percentage === undefined) return '#6f6f6f';
    if (percentage >= 50) return '#24a148';
    if (percentage >= 20) return '#f1c21b';
    return '#da1e28';
  };

  const headers = [
    { key: 'status', header: 'Status' },
    { key: 'name', header: 'Device Name' },
    { key: 'readings', header: 'Current Readings' },
    { key: 'battery', header: 'Battery' },
    { key: 'source', header: 'Data Source' },
    { key: 'processedAt', header: 'Last Updated' },
    { key: 'registrationStatus', header: 'Registration' },
  ];

  const handleOpenDeleteModal = () => {
    setDeleteError(null);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    if (isDeleting) return;
    setIsDeleteModalOpen(false);
    setDeleteError(null);
  };

  const handleConfirmDeleteGateway = async () => {
    if (!user || !gateway) {
      setDeleteError('You must be signed in to delete a gateway.');
      return;
    }

    try {
      setIsDeleting(true);
      setDeleteError(null);

      const hasFailedDeletion = gateway.deleted_at && gateway.deletion_status === 'failed';
      const nowIso = new Date().toISOString();

      const updateData = {
        deleted_at: nowIso,
        deletion_status: 'pending',
        deletion_started_at: nowIso,
      };

      if (gateway.marked_for_deletion) {
        updateData.marked_for_deletion = true;
        updateData.deletion_marked_at = nowIso;
      }

      const { error } = await supabase
        .from('gateways')
        .update(updateData)
        .eq('id', gateway.id)
        .eq('user_id', user.id);

      if (error) {
        setDeleteError(error.message || 'Failed to delete gateway. Please try again.');
        return;
      }

      // Close modal and return to gateway list; background deletion will run in Supabase
      setIsDeleteModalOpen(false);
      navigate('/dashboard/gateways');
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete gateway. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className={`gateway-detail ${isDark ? 'gateway-detail--dark' : 'gateway-detail--light'}`}>
        <div className="gateway-detail__container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <Loading description="Loading gateway details..." />
        </div>
      </div>
    );
  }

  if (error || !gateway) {
    return (
      <div className={`gateway-detail ${isDark ? 'gateway-detail--dark' : 'gateway-detail--light'}`}>
        <div className="gateway-detail__container">
          <div className="gateway-detail__header">
            <Button
              kind="ghost"
              size="sm"
              renderIcon={ArrowLeft}
              onClick={() => navigate('/dashboard/gateways')}
              className="gateway-detail__back-btn"
            >
              Back
            </Button>
          </div>
          <Tile style={{ padding: '2rem', textAlign: 'center' }}>
            <p style={{ color: isDark ? '#f4f4f4' : '#161616', fontSize: '1rem' }}>
              {error || 'Gateway not found'}
            </p>
          </Tile>
        </div>
      </div>
    );
  }

  const gatewayStatus = gatewayDataStatus.isConnected ? 'online' : (gateway.status || 'offline');

  return (
    <SubscriptionGate
      feature="gateway details and connected device monitoring"
      disabled={!hasActiveSubscription}
      showChildrenGreyed={true}
      customMessage="You need an active subscription to view gateway details, connected devices, and monitoring data. Your gateway configuration is preserved and will be accessible once you resubscribe."
    >
    <div className={`gateway-detail ${isDark ? 'gateway-detail--dark' : 'gateway-detail--light'}`}>
      <div className="gateway-detail__container">
        {activeShipmentBinding?.shipments && (
          <div style={{ marginBottom: 16 }}>
            <Tile style={{ padding: 12, borderLeft: '4px solid #0f62fe' }}>
              <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600 }}>
                Linked to active shipment
              </p>
              <p style={{ margin: '4px 0 0', fontSize: '0.8125rem', opacity: 0.85 }}>
                MQTT readings from devices on this gateway append to shipment{' '}
                {activeShipmentBinding.shipments.metadata?.product_name
                  || activeShipmentBinding.shipments.epc_uri
                  || activeShipmentBinding.shipment_id}
              </p>
              <Button
                kind="ghost"
                size="sm"
                style={{ marginTop: 8 }}
                onClick={() => navigate(`/dashboard/shipments/${activeShipmentBinding.shipment_id}`)}
              >
                View shipment
              </Button>
            </Tile>
          </div>
        )}
        {/* Header */}
        <div className="gateway-detail__header">
          <Button
            kind="ghost"
            size="sm"
            renderIcon={ArrowLeft}
            onClick={() => navigate('/dashboard/gateways')}
            className="gateway-detail__back-btn"
          >
            Back
          </Button>
          <div className="gateway-detail__header-content">
            <div className="gateway-detail__title-row">
              <div className="gateway-detail__icon-wrap">
                <Wifi size={24} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h1 className="gateway-detail__title">
                  {gateway.name || `Gateway ${gateway.mac_address?.slice(-6) || gateway.id.slice(0, 8)}`}
                </h1>
                <GatewayTypeTags gateway={gateway} showSource={false} />
              </div>
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                renderIcon={Settings}
                iconDescription={`${gateway ? getGatewayProductFamily(gateway) : 'Gateway'} admin`}
                tooltipPosition="left"
                className="gateway-detail__settings-btn"
                onClick={() => navigate(`/dashboard/gateways/${id}/settings`)}
              />
            </div>
            <div className="gateway-detail__meta">
              <div className="gateway-detail__status">
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '4px 10px',
                  borderRadius: '12px',
                  backgroundColor: gatewayDataStatus.isConnected ? '#24a148' : '#da1e28',
                  color: '#FFFFFF',
                  fontSize: '12px',
                  fontWeight: 600,
                }}>
                  {gatewayDataStatus.isConnected ? (
                    <CheckmarkFilled size={12} />
                  ) : (
                    <CloudOffline size={12} />
                  )}
                  <span>{gatewayDataStatus.isConnected ? 'LIVE' : 'OFFLINE'}</span>
                </div>
              </div>
              <span className="gateway-detail__last-sync">
                <Time size={14} /> Last seen: {gatewayDataStatus.lastDataReceived && gatewayDataStatus.lastDataReceived !== 'No data received' && gatewayDataStatus.lastDataReceived !== 'Error checking status' ? formatTimestamp(gatewayDataStatus.lastDataReceived) : (gateway.last_seen_at ? formatTimestamp(gateway.last_seen_at) : 'Never')}
              </span>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="gateway-detail__stats">
          <Tile className="gateway-detail__stat-card gateway-detail__stat-card--devices">
            <Devices size={24} />
            <div className="gateway-detail__stat-info">
              <span className="gateway-detail__stat-value">{connectedSensors.length}</span>
              <span className="gateway-detail__stat-label">Connected Devices</span>
            </div>
          </Tile>
          <Tile className="gateway-detail__stat-card gateway-detail__stat-card--online">
            <CheckmarkFilled size={24} />
            <div className="gateway-detail__stat-info">
              <span className="gateway-detail__stat-value">{onlineCount}</span>
              <span className="gateway-detail__stat-label">Online (&lt;15min)</span>
            </div>
          </Tile>
          {warningCount > 0 && (
            <Tile className="gateway-detail__stat-card gateway-detail__stat-card--warning" style={{ borderLeft: '3px solid #f1c21b' }}>
              <Time size={24} style={{ color: '#f1c21b' }} />
              <div className="gateway-detail__stat-info">
                <span className="gateway-detail__stat-value">{warningCount}</span>
                <span className="gateway-detail__stat-label">Stale (15-60min)</span>
              </div>
            </Tile>
          )}
          <Tile className="gateway-detail__stat-card gateway-detail__stat-card--offline">
            <CloudOffline size={24} />
            <div className="gateway-detail__stat-info">
              <span className="gateway-detail__stat-value">{offlineCount}</span>
              <span className="gateway-detail__stat-label">Offline (&gt;60min)</span>
            </div>
          </Tile>
        </div>

        {/* Connection Status Card */}
        <Tile className="gateway-detail__info-card">
          <h3 className="gateway-detail__section-title">Connection Status</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: isDark ? '#c6c6c6' : '#525252', fontSize: '0.875rem' }}>Connection Status:</span>
              <span style={{ 
                color: gatewayDataStatus.isConnected ? '#24a148' : '#da1e28', 
                fontSize: '0.875rem',
                fontWeight: 600
              }}>
                {gatewayDataStatus.isConnected ? 'Connected' : 'Not Connected'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: isDark ? '#c6c6c6' : '#525252', fontSize: '0.875rem' }}>Last Data:</span>
              <span style={{ color: isDark ? '#f4f4f4' : '#161616', fontSize: '0.875rem' }}>
                {gatewayDataStatus.lastDataReceived ? formatTimestamp(gatewayDataStatus.lastDataReceived) : 'No data received'}
              </span>
            </div>
            {gatewayDataStatus.dataCount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: isDark ? '#c6c6c6' : '#525252', fontSize: '0.875rem' }}>
                  Records in last {DATA_WINDOW_MINUTES} min:
                </span>
                <span style={{ color: isDark ? '#f4f4f4' : '#161616', fontSize: '0.875rem' }}>
                  {gatewayDataStatus.dataCount} records
                </span>
              </div>
            )}
            {gatewayConnectionSource && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: isDark ? '#c6c6c6' : '#525252', fontSize: '0.875rem' }}>Connection Type:</span>
                <Tag type={gatewayConnectionSource === 'http' ? 'cyan' : 'purple'} size="sm">
                  {gatewayConnectionSource.toUpperCase()}
                </Tag>
              </div>
            )}
          </div>
        </Tile>

        {/* Gateway Health Card */}
        {gatewayHealth && (
          <Tile className="gateway-detail__info-card">
            <h3 className="gateway-detail__section-title">Gateway Health</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: isDark ? '#c6c6c6' : '#525252', fontSize: '0.875rem' }}>Latest heartbeat:</span>
                <span style={{ color: isDark ? '#f4f4f4' : '#161616', fontSize: '0.875rem' }}>
                  {formatHeartbeatTime(gatewayHealth.heartbeatAt)}
                </span>
              </div>
              {gatewayHealth.previousHeartbeatAt && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: isDark ? '#c6c6c6' : '#525252', fontSize: '0.875rem' }}>Previous heartbeat:</span>
                  <span style={{ color: isDark ? '#f4f4f4' : '#161616', fontSize: '0.875rem' }}>
                    {formatHeartbeatTime(gatewayHealth.previousHeartbeatAt)}
                  </span>
                </div>
              )}
              {gatewayHealth.intervalSeconds !== undefined && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: isDark ? '#c6c6c6' : '#525252', fontSize: '0.875rem' }}>Heartbeat interval:</span>
                  <span style={{ color: isDark ? '#f4f4f4' : '#161616', fontSize: '0.875rem' }}>
                    {gatewayHealth.intervalSeconds < 60 
                      ? `${gatewayHealth.intervalSeconds}s`
                      : `${Math.floor(gatewayHealth.intervalSeconds / 60)} min ${gatewayHealth.intervalSeconds % 60}s`}
                  </span>
                </div>
              )}
              {gatewayHealth.uptime !== undefined && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: isDark ? '#c6c6c6' : '#525252', fontSize: '0.875rem' }}>Gateway uptime:</span>
                  <span style={{ color: isDark ? '#f4f4f4' : '#161616', fontSize: '0.875rem' }}>
                    {formatUptime(gatewayHealth.uptime)}
                  </span>
                </div>
              )}
              {gatewayHealth.firmware && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: isDark ? '#c6c6c6' : '#525252', fontSize: '0.875rem' }}>Firmware:</span>
                  <span style={{ color: isDark ? '#f4f4f4' : '#161616', fontSize: '0.875rem' }}>
                    {gatewayHealth.firmware} {gatewayHealth.blever ? `(BLE ${gatewayHealth.blever})` : ''}
                  </span>
                </div>
              )}
              {gatewayHealth.wanIP && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: isDark ? '#c6c6c6' : '#525252', fontSize: '0.875rem' }}>WAN IP:</span>
                  <span style={{ color: isDark ? '#f4f4f4' : '#161616', fontSize: '0.875rem', fontFamily: 'monospace' }}>
                    {gatewayHealth.wanIP}
                  </span>
                </div>
              )}
              {(gatewayHealth.latitude !== undefined && gatewayHealth.longitude !== undefined) && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: isDark ? '#c6c6c6' : '#525252', fontSize: '0.875rem' }}>Location/Altitude:</span>
                  <span style={{ color: isDark ? '#f4f4f4' : '#161616', fontSize: '0.875rem', fontFamily: 'monospace' }}>
                    {gatewayHealth.latitude.toFixed(6)}, {gatewayHealth.longitude.toFixed(6)}
                    {gatewayHealth.altitude !== undefined ? ` • ${gatewayHealth.altitude.toFixed(1)} m` : ''}
                  </span>
                </div>
              )}
              {(gatewayHealth.load || gatewayHealth.temp !== undefined || gatewayHealth.mem_free !== undefined) && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: isDark ? '#c6c6c6' : '#525252', fontSize: '0.875rem' }}>System:</span>
                  <span style={{ color: isDark ? '#f4f4f4' : '#161616', fontSize: '0.875rem' }}>
                    {gatewayHealth.load || '—'} • {gatewayHealth.temp !== undefined ? `${gatewayHealth.temp}°C` : '—'} • {gatewayHealth.mem_free !== undefined ? `${gatewayHealth.mem_free}MB free` : '—'}
                  </span>
                </div>
              )}
            </div>
          </Tile>
        )}

        {/* Gateway Info */}
        <Tile className="gateway-detail__info-card">
          <h3 className="gateway-detail__section-title">Gateway Information</h3>
          <div className="gateway-detail__info-grid">
            <div className="gateway-detail__info-item">
              <span className="gateway-detail__info-label">MAC Address</span>
              <span className="gateway-detail__info-value gateway-detail__info-value--mono">
                {gateway.mac_address || 'N/A'}
              </span>
            </div>
            {gateway.ip_address && (
            <div className="gateway-detail__info-item">
              <span className="gateway-detail__info-label">IP Address</span>
                <span className="gateway-detail__info-value gateway-detail__info-value--mono">
                  {gateway.ip_address}
                </span>
            </div>
            )}
            <div className="gateway-detail__info-item">
              <span className="gateway-detail__info-label">Added</span>
              <span className="gateway-detail__info-value">
                {gateway.created_at ? formatRelativeTime(new Date(gateway.created_at)) : 'N/A'}
              </span>
            </div>
            {gateway.provisioned_at && (
            <div className="gateway-detail__info-item">
                <span className="gateway-detail__info-label">Provisioned</span>
                <span className="gateway-detail__info-value">
                  {formatRelativeTime(new Date(gateway.provisioned_at))}
                </span>
            </div>
            )}
          </div>
        </Tile>

        {/* Sensor Assignment Card */}
        <Tile className="gateway-detail__info-card">
          <h3 className="gateway-detail__section-title">Sensor Assignment</h3>
          <p style={{ 
            color: isDark ? '#c6c6c6' : '#525252', 
            fontSize: '0.875rem', 
            marginBottom: '1rem' 
          }}>
            Assign sensors to this gateway for automatic data collection
          </p>
          
          {!allSensors || allSensors.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: isDark ? '#c6c6c6' : '#525252' }}>
              <p style={{ marginBottom: '1rem' }}>No sensors found</p>
              <p style={{ fontSize: '0.875rem', marginBottom: '1rem' }}>
                Add sensors to your account to assign them to this gateway
              </p>
              <Button kind="tertiary" size="sm" renderIcon={Add} onClick={() => navigate('/dashboard/devices/add')}>
                Add Sensor
              </Button>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {paginatedSensors.map((sensor) => (
                  <div
                    key={sensor.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.75rem',
                      borderBottom: `1px solid ${isDark ? '#393939' : '#e0e0e0'}`,
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        color: isDark ? '#f4f4f4' : '#161616', 
                        fontWeight: 500, 
                        marginBottom: '0.25rem',
                        fontSize: '0.9375rem'
                      }}>
                        {getSensorDisplayName(sensor)}
            </div>
                      <div style={{ 
                        color: isDark ? '#8d8d8d' : '#6f6f6f', 
                        fontSize: '0.75rem',
                        fontFamily: 'monospace'
                      }}>
                        {getDisplayIdentifier(sensor)}
            </div>
          </div>
                    <Toggle
                      id={`toggle-${sensor.id}`}
                      labelText=""
                      toggled={connectedSensors.some(connectedSensor => connectedSensor.id === sensor.id)}
                      onToggle={(checked) => handleSensorToggle(sensor.id, checked)}
                      disabled={isToggling}
                      size="sm"
                    />
                  </div>
                ))}
              </div>
              
              {/* Pagination */}
              {totalSensorPages > 1 && (
                <div style={{ 
                  marginTop: '1rem', 
                  paddingTop: '1rem', 
                  borderTop: `1px solid ${isDark ? '#393939' : '#e0e0e0'}` 
                }}>
                  <Pagination
                    page={sensorPage}
                    pageSize={sensorPageSize}
                    pageSizes={[5, 10, 20, 50]}
                    totalItems={allSensors.length}
                    onChange={({ page, pageSize }) => {
                      setSensorPage(page);
                      setSensorPageSize(pageSize);
                    }}
                  />
                </div>
              )}
            </>
          )}
        </Tile>

        {/* Connected Devices Table */}
        <Tile className="gateway-detail__devices-card">
          <div className="gateway-detail__devices-header">
            <h3 className="gateway-detail__section-title">Connected Devices</h3>
            <div style={{ display: 'flex', gap: '4px' }}>
              <Tag type="green" size="sm">
                {onlineCount} Online
              </Tag>
              {warningCount > 0 && (
                <Tag type="warm-gray" size="sm">
                  {warningCount} Stale
                </Tag>
              )}
              {offlineCount > 0 && (
                <Tag type="red" size="sm">
                  {offlineCount} Offline
                </Tag>
              )}
            </div>
          </div>
          
          {sensorsWithReadings.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: isDark ? '#c6c6c6' : '#525252' }}>
              <p>No sensors assigned to this gateway.</p>
            </div>
          ) : (
            <>
          {/* Mobile: Card View */}
          <div className="gateway-detail__devices-mobile">
                {paginatedDevices.map((device) => (
              <div
                key={device.id}
                className={`gateway-detail__device-card gateway-detail__device-card--${device.status}`}
                onClick={() => navigate(`/dashboard/devices/${device.id}`)}
              >
                <div className="gateway-detail__device-card-header">
                  <div className="gateway-detail__device-name-wrap">
                    {getStatusIcon(device.status)}
                        <span className="gateway-detail__device-name">{device.name || device.device_id}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <Tag type={getStatusTagType(device.status)} size="sm">
                      {device.status === 'warning' ? 'stale' : device.status}
                    </Tag>
                    {device.source && device.source !== 'UNKNOWN' && (
                      <Tag type={device.source === 'MQTT' ? 'purple' : device.source === 'HTTP' ? 'cyan' : 'blue'} size="sm">
                        {device.source}
                      </Tag>
                    )}
                  </div>
                </div>
                <div className="gateway-detail__device-card-body">
                  <div className="gateway-detail__device-readings">
                        {device.temperature !== null && device.temperature !== undefined && typeof device.temperature === 'number' && (
                      <span className="gateway-detail__device-reading">
                            <Temperature size={14} /> {device.temperature.toFixed(1)}°C
                      </span>
                    )}
                        {device.humidity !== null && device.humidity !== undefined && typeof device.humidity === 'number' && (
                      <span className="gateway-detail__device-reading">
                            <Humidity size={14} /> {device.humidity.toFixed(1)}%
                      </span>
                    )}
                        {((device.temperature === null || device.temperature === undefined || typeof device.temperature !== 'number') && 
                         (device.humidity === null || device.humidity === undefined || typeof device.humidity !== 'number')) && (
                      <span className="gateway-detail__device-reading gateway-detail__device-reading--na">No data</span>
                    )}
                  </div>
                  <div className="gateway-detail__device-meta">
                        {device.battery !== null && device.battery !== undefined ? (
                    <span style={{ color: getBatteryColor(device.battery) }}>
                      {device.battery}% battery
                    </span>
                        ) : (
                          <span style={{ color: '#6f6f6f' }}>N/A</span>
                        )}
                  </div>
                  <div className="gateway-detail__device-time">
                    <Time size={12} /> {device.processedAt || 'No data'}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: Table View */}
          <div className="gateway-detail__devices-table">
                <DataTable rows={paginatedDevices} headers={headers} isSortable={false}>
              {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                <Table {...getTableProps()} size="lg">
                  <TableHead>
                    <TableRow>
                      {headers.map((header) => {
                        const { key, ...headerProps } = getHeaderProps({ header });
                        return (
                          <TableHeader key={header.key} {...headerProps}>
                            {header.header}
                          </TableHeader>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                        {paginatedDevices.map((device) => (
                      <TableRow 
                        key={device.id} 
                        onClick={() => navigate(`/dashboard/devices/${device.id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <div className="gateway-detail__table-status">
                            {getStatusIcon(device.status)}
                            <span style={{ color: getStatusColor(device.status) }}>
                              {device.status === 'warning' ? 'stale' : device.status}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="gateway-detail__table-device">
                                <span className="gateway-detail__table-device-name">
                                  {device.name || device.device_id}
                                </span>
                                <span className="gateway-detail__table-device-type">
                                  {device.device_type || 'Sensor'}
                                </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="gateway-detail__table-readings">
                                {device.temp !== null && device.temp !== undefined && typeof device.temp === 'number' && (
                                  <span><Temperature size={14} /> {device.temp.toFixed(1)}°C</span>
                            )}
                                {device.humidity !== null && device.humidity !== undefined && typeof device.humidity === 'number' && (
                                  <span><Humidity size={14} /> {device.humidity.toFixed(1)}%</span>
                            )}
                                {((device.temp === null || device.temp === undefined || typeof device.temp !== 'number') && 
                                 (device.humidity === null || device.humidity === undefined || typeof device.humidity !== 'number')) && (
                              <span style={{ color: '#6f6f6f' }}>No gateway data</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                              {device.battery !== null && device.battery !== undefined ? (
                          <span style={{ color: getBatteryColor(device.battery) }}>
                                  {Math.round(device.battery)}%
                          </span>
                              ) : (
                                <span style={{ color: '#6f6f6f' }}>N/A</span>
                              )}
                        </TableCell>
                        <TableCell>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {device.source && device.source !== 'UNKNOWN' ? (
                              <Tag type={device.source === 'MQTT' ? 'purple' : device.source === 'HTTP' ? 'cyan' : 'blue'} size="sm">
                                {device.source}
                              </Tag>
                            ) : (
                              <Tag type="gray" size="sm">UNKNOWN</Tag>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="gateway-detail__table-time" style={{
                            color: device.processedAt && device.processedAt !== 'No data' ? (isDark ? '#f4f4f4' : '#161616') : (isDark ? '#8d8d8d' : '#6f6f6f'),
                            fontWeight: device.processedAt && device.processedAt !== 'No data' ? 500 : 400
                          }}>
                            {device.processedAt || 'No data'}
                          </span>
                        </TableCell>
                        <TableCell>
                          {device.registrationStatus ? (
                            <Tag type={device.registrationStatus === 'registered' ? 'green' : device.registrationStatus === 'unregistered' ? 'red' : 'gray'} size="sm">
                              {device.registrationStatus}
                            </Tag>
                          ) : (
                            <span style={{ color: '#6f6f6f' }}>N/A</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </DataTable>
          </div>
              
              {/* Pagination for Connected Devices Table */}
              {totalDevices > 0 && (
                <div style={{ 
                  marginTop: '16px', 
                  display: 'flex', 
                  justifyContent: 'center'
                }}>
                  <Pagination
                    backwardText="Previous"
                    forwardText="Next"
                    itemsPerPageText="Items per page:"
                    page={devicesPage}
                    pageNumberText="Page Number"
                    pageSize={devicesPageSize}
                    pageSizes={[10, 20, 30, 40, 50]}
                    size="md"
                    totalItems={totalDevices}
                    onChange={({ page, pageSize }) => {
                      setDevicesPage(page);
                      setDevicesPageSize(pageSize);
                    }}
                  />
                </div>
              )}
            </>
          )}
        </Tile>

        {/* Actions Card */}
        <Tile className="gateway-detail__actions-card">
          <h3 className="gateway-detail__section-title">Gateway Actions</h3>
          <div className="gateway-detail__actions">
            <Button
              kind="tertiary"
              size="sm"
              renderIcon={Settings}
              onClick={() => navigate(`/dashboard/gateways/${id}/settings`)}
            >
              Settings
            </Button>
            <Button kind="tertiary" size="sm" renderIcon={Restart}>
              Refresh
            </Button>
            <Button
              kind="danger--tertiary"
              size="sm"
              renderIcon={TrashCan}
              onClick={handleOpenDeleteModal}
              disabled={isDeleting}
            >
              Remove Gateway
            </Button>
          </div>
        </Tile>
      </div>
      {/* Delete Gateway Confirmation Modal */}
      <Modal
        open={isDeleteModalOpen}
        modalHeading={gateway?.deletion_status === 'failed' ? 'Retry Gateway Deletion' : 'Delete Gateway'}
        primaryButtonText={gateway?.deletion_status === 'failed' ? 'Retry Delete' : 'Delete'}
        secondaryButtonText="Cancel"
        danger
        onRequestClose={handleCloseDeleteModal}
        onRequestSubmit={handleConfirmDeleteGateway}
        primaryButtonDisabled={isDeleting}
      >
        <p style={{ marginBottom: '1rem' }}>
          {gateway?.deletion_status === 'failed'
            ? `Previous deletion attempt failed. Do you want to retry deleting "${gateway?.name || 'this gateway'}"? This will reset the deletion status and begin the deletion process again.`
            : `Are you sure you want to delete "${gateway?.name || 'this gateway'}"? This will permanently remove the gateway and all its data from your account. This action cannot be undone.`}
        </p>
        {deleteError && (
          <p style={{ color: '#da1e28', fontSize: '0.875rem', marginTop: '0.5rem' }}>
            {deleteError}
          </p>
        )}
        {isDeleting && (
          <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
            Deleting gateway...
          </p>
        )}
      </Modal>
    </div>
    </SubscriptionGate>
  );
};

export default GatewayDetailPage;